import { __decorate } from "tslib";
import { mixins } from 'vue-class-component';
import { Component, Prop } from 'vue-property-decorator';
import ActionButton from '@/components/action_button/Index.vue';
import FormHeader from '@/components/v2/FormHeader.vue';
import { required } from 'vuelidate/lib/validators';
import { requiredProperty, successfulCreate } from '@/helpers/mixins/LocalizationHelper';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import BaseDatetimePicker from '@/components/datetime_field/Index.vue';
import OrderStatusUpdate from '@/plugins/graphql/mutations/v2/order-status-update.gql';
import moment from 'moment';
import EcommerceOrder from '@/plugins/graphql/queries/v2/order.gql';
import CreditsWidget from '@/plugins/graphql/queries/v2/grab-express-credits.gql';
import OrderStatusActionMixin from '@/helpers/vue_mixins/v2/ecommerce/OrderStatusActionMixin';
import AppAccumulatedFee from '@/plugins/graphql/queries/v2/app-accumulated-fees.gql';
import { FULL_DATETIME_FORMAT } from '@/helpers/constants/app';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import { Mutation } from 'vuex-class';
import { ORDER } from '@/helpers/constants/Store';
import { ShippingMethodThirdPartyProvider } from '@/helpers/entities/enums/v2/ShippingMethodThirdPartyProvider';
import OrderStatusBulkUpdate from '@/plugins/graphql/mutations/v2/order-status-bulk-update.gql';
import { PaymentOptionType } from '@/helpers/entities/enums/v2/PaymentOptionType';
import { OrderStatuses } from '@/helpers/entities/enums/v2/OrderStatuses';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
const namespace = { namespace: ORDER };
let OrderConfirmationForm = class OrderConfirmationForm extends mixins(OrderStatusActionMixin, ViewportSizeMixin, CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.shipOutLabel = 'Ship Out Date';
        this.form = {
            shipOutDate: this.defaultEstimateShipOut,
            estimateArriveDate: this.defaultEstimateArrival,
            additionalNotes: null,
        };
        this.confirmCheckbox = this.isPaid;
    }
    get defaultEstimateShipOut() {
        const current = new Date();
        if (this.grabExpressShipping ||
            this.lalamoveShipping ||
            this.pandagoShipping ||
            this.jntExpressShipping) {
            current.setMinutes(current.getMinutes() + 15);
            return this.formatDate(FULL_DATETIME_FORMAT, current.toISOString());
            // Set Ship Out Date + 15 mins for Grab Express Delivery
        }
        if (this.ninjaVanShipping && this.pickUpStartAt && this.pickUpEndAt) {
            const deliveryDate = moment().add(1, 'days');
            const [deliveryTime] = this.pickUpStartAt.split(':');
            deliveryDate.set('hour', Number(deliveryTime));
            deliveryDate.set('minute', 0);
            return this.formatDate(FULL_DATETIME_FORMAT, deliveryDate.toISOString());
        }
        return this.formatDate(FULL_DATETIME_FORMAT, current.toISOString());
    }
    get defaultEstimateArrival() {
        const current = new Date();
        if (this.grabExpressShipping ||
            this.lalamoveShipping ||
            this.pandagoShipping ||
            this.jntExpressShipping) {
            current.setHours(current.getHours() + 1);
            return this.formatDate(FULL_DATETIME_FORMAT, current.toISOString());
            // Set Estimate Arrival Date + 1 hour for Grab Express Delivery
        }
        if (this.ninjaVanShipping && this.pickUpStartAt && this.pickUpEndAt) {
            const shipOutDate = moment().add(4, 'days');
            const [pickUpTime] = this.pickUpStartAt.split(':');
            shipOutDate.set('hour', Number(pickUpTime));
            shipOutDate.set('minute', 0);
            return this.formatDate(FULL_DATETIME_FORMAT, shipOutDate.toISOString());
        }
        return this.formatDate(FULL_DATETIME_FORMAT, current.toISOString());
    }
    get grabExpressShipping() {
        if (this.shippingProvider) {
            return this.shippingProvider === ShippingMethodThirdPartyProvider.GrabExpress;
        }
        return false;
    }
    get lalamoveShipping() {
        if (this.shippingProvider) {
            return this.shippingProvider === ShippingMethodThirdPartyProvider.Lalamove;
        }
        return false;
    }
    get pandagoShipping() {
        if (this.shippingProvider) {
            return this.shippingProvider === ShippingMethodThirdPartyProvider.PandaGo;
        }
        return false;
    }
    get ninjaVanShipping() {
        if (this.shippingProvider) {
            return this.shippingProvider === ShippingMethodThirdPartyProvider.NinjaVan;
        }
        return false;
    }
    get jntExpressShipping() {
        if (this.shippingProvider) {
            return this.shippingProvider === ShippingMethodThirdPartyProvider.JntExpress;
        }
        return false;
    }
    get shipOutDateErrors() {
        const errors = [];
        if (!this.$v.form.shipOutDate.$dirty) {
            return errors;
        }
        if (!this.$v.form.shipOutDate.required) {
            const msg = requiredProperty('ShipOut Date');
            errors.push(msg);
        }
        if (!this.$v.form.shipOutDate.shipOutDateNotPast) {
            errors.push('Ship out Date should have 30 minutes interval from the current time');
        }
        if (!this.$v.form.shipOutDate.validShipOutDate) {
            errors.push('Ship Out Date must be less than or equal Estimated Arrival Date');
        }
        return errors;
    }
    get estimateArriveDateErrors() {
        const errors = [];
        if (!this.$v.form.estimateArriveDate.$dirty) {
            return errors;
        }
        if (!this.$v.form.estimateArriveDate.required) {
            const msg = requiredProperty('Estimated Arrival Date');
            errors.push(msg);
        }
        if (!this.$v.form.estimateArriveDate.mustNotBeBeforeShipOutDate) {
            errors.push('Estimated Arrival Date should not be earlier than Ship Out Date');
        }
        return errors;
    }
    onSubmit() {
        this.$v.$touch();
        if (!this.$v.$invalid) {
            if (this.orderStatusUuids.length >= 2) {
                this.submitSelectedOrders(this.form);
            }
            else {
                this.submit(this.form);
            }
        }
    }
    async submit(form) {
        this.requestStart();
        await this.create(form)
            .then(() => {
            if (this.formHeader === 'Rebook Order' && this.shippingProvider === 'jnt_express') {
                this.toggle = false;
            }
            /**
             * Trigger Mutation for Hiding Order Details Dialog
             * Unfortunately `emitter` doesn't work since this component is already
             * destroyed upon sending mutation
             * @author Jimson Lomio <jimson@gorated.ph>
             * @since Version {0.17.0}
             */
            if (this.xsOnly) {
                this.hideOrderDetails();
            }
            const msg = successfulCreate('Ship Out Details');
            this.orderAction('CONFIRM ORDER');
            this.successNotification(msg);
            this.$router.push({
                name: 'ecommerce-orders-by-status',
                params: { status: 'for_delivery' },
            });
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    async create(form) {
        const { additionalNotes } = form;
        const appUuid = this.appUUID;
        const currentStatus = OrderStatuses.forDelivery;
        const prevStatus = OrderStatuses.pending;
        const vm = this;
        const parsedShipOutDate = form.shipOutDate ? moment(form.shipOutDate).format() : '';
        const parsedArrivalDate = form.estimateArriveDate
            ? moment(form.estimateArriveDate).format()
            : '';
        return this.$apollo.mutate({
            mutation: OrderStatusUpdate,
            variables: {
                input: {
                    shipOutDate: parsedShipOutDate,
                    estimateArriveDate: parsedArrivalDate,
                    additionalNotes,
                    orderStatusUuid: this.orderStatusUuid,
                    status: currentStatus,
                    todoAction: 'confirm_order',
                },
            },
            update(cache, { data }) {
                const updatedOrderStatus = data.orderStatusUpdate.orderStatus;
                if (updatedOrderStatus.order.paymentOption.paymentType === PaymentOptionType.creditCard ||
                    updatedOrderStatus.order.paymentOption.paymentType === PaymentOptionType.thirdParty) {
                    return;
                }
                vm.updateOrderStatusCache(prevStatus, currentStatus, updatedOrderStatus, vm.orderUuid, appUuid, cache);
            },
            refetchQueries: [
                {
                    query: EcommerceOrder,
                    variables: {
                        orderUuid: vm.orderUuid,
                    },
                },
                {
                    query: AppAccumulatedFee,
                    variables: {
                        appUuid,
                    },
                },
                {
                    query: CreditsWidget,
                    variables: {
                        appUuid,
                    },
                },
            ],
        });
    }
    async submitSelectedOrders(form) {
        this.requestStart();
        await this.updateSelectedOrder(form)
            .then(() => {
            this.successNotification('Ship Out Details');
            if (this.status === 'pending') {
                this.$router.push({
                    name: 'orders-by-status',
                    params: { status: 'for_delivery' },
                });
            }
            if (this.status === 'for_delivery') {
                this.toggle = false;
                this.$emit('success');
            }
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    async updateSelectedOrder(form) {
        const parsedShipOutDate = form.shipOutDate ? moment(form.shipOutDate).format() : '';
        const parsedArrivalDate = form.estimateArriveDate
            ? moment(form.estimateArriveDate).format()
            : '';
        return this.$apollo.mutate({
            mutation: OrderStatusBulkUpdate,
            variables: {
                input: {
                    orderStatuses: this.orderStatusUuids,
                    status: 'for_delivery',
                    shipOutDate: parsedShipOutDate,
                    estimateArriveDate: parsedArrivalDate,
                },
            },
        });
    }
    get requiredCheckbox() {
        const errors = [];
        if (!this.$v.confirmCheckbox.$dirty) {
            return errors;
        }
        if (!this.$v.confirmCheckbox.reqCheckBox) {
            const msg = 'You must confirm that you acknowledge the warning regarding unpaid orders before proceeding.';
            errors.push(msg);
        }
        return errors;
    }
    shouldTrue() {
        return this.confirmCheckbox;
    }
};
__decorate([
    Prop({ type: Boolean, required: false })
], OrderConfirmationForm.prototype, "isThirdPartyPayment", void 0);
__decorate([
    Prop({ type: Boolean, required: false })
], OrderConfirmationForm.prototype, "isPaid", void 0);
__decorate([
    Prop({ type: Boolean, required: false })
], OrderConfirmationForm.prototype, "isBank", void 0);
__decorate([
    Prop({ type: String, required: true })
], OrderConfirmationForm.prototype, "formHeader", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], OrderConfirmationForm.prototype, "thirdPartyShipping", void 0);
__decorate([
    Prop({ type: String })
], OrderConfirmationForm.prototype, "shippingProvider", void 0);
__decorate([
    Prop({ type: String })
], OrderConfirmationForm.prototype, "pickUpStartAt", void 0);
__decorate([
    Prop({ type: String })
], OrderConfirmationForm.prototype, "pickUpEndAt", void 0);
__decorate([
    Prop({ type: Array, default: () => [] })
], OrderConfirmationForm.prototype, "orderStatusUuids", void 0);
__decorate([
    Prop({ type: String })
], OrderConfirmationForm.prototype, "status", void 0);
__decorate([
    Mutation('hideOrderDetails', namespace)
], OrderConfirmationForm.prototype, "hideOrderDetails", void 0);
OrderConfirmationForm = __decorate([
    Component({
        components: {
            ActionButton,
            FormHeader,
            BaseTextField,
            BaseDatetimePicker,
        },
        validations() {
            const vm = this;
            const { thirdPartyShipping } = vm;
            return {
                form: {
                    shipOutDate: {
                        required,
                        shipOutDateNotPast: (val) => {
                            const current = moment();
                            const shipOut = moment(val);
                            /**
                             * Ensure Ship Out is not a past Date
                             * Validate
                             *  - Date => Internal shipping
                             *  - DateTime => ThirdParty shipping
                             *
                             * @author Peter John Alvarado <peterjohn@gorated.ph>
                             * @since Version {0.4.1}
                             */
                            if (thirdPartyShipping) {
                                const shipOutDateTime = shipOut.format();
                                const currentDateTime = current.format();
                                return shipOutDateTime > currentDateTime;
                            }
                            return true;
                        },
                        validShipOutDate: (val) => {
                            const arrival = moment(vm.form.estimateArriveDate);
                            const shipOut = moment(val);
                            /**
                             * Ensure Ship Out is valid
                             * Validate
                             * - Date => Internal Shipping
                             * - DateTime => ThirdParty Shipping
                             *
                             * @author Peter John Alvarado <peterjohn@gorated.ph>
                             * @since Version {0.4.1}
                             */
                            if (thirdPartyShipping) {
                                const shipOutDateTime = shipOut.format();
                                const arrivalDateTime = arrival.format();
                                return shipOutDateTime < arrivalDateTime;
                            }
                            return true;
                        },
                    },
                    estimateArriveDate: {
                        required,
                        mustNotBeBeforeShipOutDate() {
                            const { shipOutDate } = this.form;
                            const { estimateArriveDate } = this.form;
                            const parsedShipOutDate = moment(shipOutDate).format();
                            const parsedEstimatedArriveDate = moment(estimateArriveDate).format();
                            return parsedEstimatedArriveDate >= parsedShipOutDate;
                        },
                    },
                    additionalNotes: {},
                },
                confirmCheckbox: {
                    reqCheckBox() {
                        if (!vm.isThirdPartyPayment && !vm.isBank) {
                            return true;
                        }
                        return vm.shouldTrue();
                    },
                },
            };
        },
    })
], OrderConfirmationForm);
export default OrderConfirmationForm;
